/**
 * Floating-card search-again surface (Option 2, 2026-06-08 redesign).
 *
 * Replaces the previous sticky frosted band. Lives below the global
 * SiteHeader on every /compare and /c/[slug] surface, inside
 * <main className="cmp-surface ..."> so it inherits the cool-hue
 * 240 palette. Centered card with rounded corners, breathing room
 * above (32px), a small "Looking for something else?" label, and
 * the input + submit pair underneath. No sticky behaviour.
 *
 * Pattern: Trivago / Booking.com / Kayak hero-search-on-results-page.
 * The card affordance reads as "you can start a new search here"
 * rather than "this is a utility bar". Premium feel matching the
 * 2026-06-08 mockup C aesthetic.
 *
 * Submit behaviour: router.push("/compare?q=<encoded>") with NO
 * from_chip param so clarify re-runs for the new query (matches the
 * /compare route's existing search-fresh contract).
 *
 * No autoFocus. No global keyboard shortcut. Disabled while a submit
 * is in flight to prevent double navigation.
 */

"use client";

import { FormEvent, useState, useTransition } from "react";
import { usePathname, useRouter } from "next/navigation";
import type { Route } from "next";
import { ArrowRight } from "lucide-react";

import { trackSearchSubmitted } from "@/lib/analytics";
import { useGhostAutocomplete } from "@/lib/hooks/useGhostAutocomplete";
import { GhostLayer } from "@/components/search/GhostLayer";
import { cn } from "@/lib/utils";

// Feature flag mirror — same env var as HomeSearchInput. When OFF
// the hook short-circuits and this component renders identically
// to its pre-B-117 form.
const AUTOCOMPLETE_ENABLED =
  process.env.NEXT_PUBLIC_AUTOCOMPLETE_ENABLED === "true";

type Props = {
  /** The current page's search query, pre-filled into the input. */
  initialQuery: string;
  /** Optional placeholder override (e.g. the ask screen's "type the exact
   *  product"). Defaults to the global text so every existing caller is
   *  byte-identical. */
  placeholder?: string;
  /** When set, submit calls this with the trimmed query INSTEAD of navigating
   *  to /compare. The searching overlay uses it to background the CURRENT
   *  search first (so it keeps running in the tray), then start the new one.
   *  Unset (every existing caller) = the original navigate behaviour. */
  onSubmitQuery?: (query: string) => void;
};

export function SearchAgainBar({
  initialQuery,
  placeholder = "Search any product...",
  onSubmitQuery,
}: Props) {
  const router = useRouter();
  const pathname = usePathname() || "";
  const [value, setValue] = useState(initialQuery);
  const [isPending, startTransition] = useTransition();
  const auto = useGhostAutocomplete(value, setValue, {
    enabled: AUTOCOMPLETE_ENABLED,
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const q = value.trim();
    if (!q || isPending) return;
    // Overlay override: hand the query to the caller (which backgrounds the
    // current search, then navigates) instead of the default /compare push.
    if (onSubmitQuery) {
      onSubmitQuery(q);
      return;
    }
    // Discriminate the source so GA funnels can attribute resubmits
    // to the surface the user was on (product page, list page,
    // or the streaming /compare page).
    const source: "c_slug" | "c_list_slug" | "compare" | "other" =
      pathname.startsWith("/c/list/")
        ? "c_list_slug"
        : pathname.startsWith("/c/")
          ? "c_slug"
          : pathname.startsWith("/compare")
            ? "compare"
            : "other";
    trackSearchSubmitted({ query: q, source });
    startTransition(() => {
      // 2026-07-31: this bar is a GLOBAL search box - it behaves exactly
      // like typing on the home page for ANY query, including the same
      // product the user is already viewing. `/compare` runs resolveQuery
      // and redirects to the saved /c page INSTANTLY when one exists, or
      // runs a fresh search only when it does not. Previously, re-submitting
      // the SAME product appended `?r=<ts>` to force-skip the cache and run
      // a fresh (~$0.017, ~60-75s) search - surprising and costly. Removed:
      // the same query now loads its saved page instantly like any other.
      router.push(`/compare?q=${encodeURIComponent(q)}` as Route);
    });
  }

  return (
    <>
      {/* Live region is a SIBLING of the card, not a descendant, because
          the form carries aria-busy={isPending}. Per WAI-ARIA 1.2 §6.6.1,
          announcements within an aria-busy subtree are deferred until
          busy=false, at which point this ternary has already cleared the
          text and the announcement is silently dropped. */}
      <span role="status" aria-live="polite" className="mio-sr-only">
        {isPending ? `Searching for ${value.trim()}` : ""}
      </span>
      {/* 2026-08-23: renders the SAME field as the landing page
          (components/homepage/HomeSearchInput.tsx) so the search box is
          identical across the home, comparison, category and browse pages.
          Keep this visual in sync with HomeSearchInput. Behaviour (initialQuery
          prefill, onSubmitQuery override, source tracking, no autoFocus) is
          unchanged. */}
      <form
        onSubmit={onSubmit}
        role="search"
        aria-label="Search a different product"
        aria-busy={isPending}
        className="relative mx-auto mt-8 mb-6 w-full"
        style={{ maxWidth: 660 }}
      >
        <div
          className={cn(
            "group relative flex items-center gap-3",
            "border bg-white/[0.05] backdrop-blur-2xl",
            "transition-shadow duration-200",
          )}
          style={{
            borderColor: "rgba(255,255,255,0.12)",
            borderRadius: 20,
            padding: "6px 6px 6px clamp(16px, 5vw, 24px)",
            boxShadow:
              "0 24px 64px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)",
          }}
        >
          <span
            aria-hidden="true"
            className="flex-shrink-0 text-accent"
            style={{ fontSize: "1.25rem", lineHeight: 1 }}
          >
            ✦
          </span>
          <span style={{ position: "relative", display: "flex", flex: 1, minWidth: 0, alignItems: "center" }}>
            <GhostLayer
              userValue={value}
              suffix={auto.ghost}
              inputStyle={{
                fontSize: "clamp(14px, 4vw, 18px)",
                padding: "20px 4px",
                letterSpacing: "-0.005em",
              }}
            />
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={auto.onKeyDown}
              onCompositionStart={auto.onCompositionStart}
              onCompositionEnd={auto.onCompositionEnd}
              placeholder={placeholder}
              disabled={isPending}
              spellCheck={false}
              autoComplete="off"
              aria-label={placeholder}
              className={cn(
                "flex-1 bg-transparent mio-search-input",
                "text-foreground",
                "outline-none focus-visible:outline-none",
                "disabled:opacity-50",
                "relative",
              )}
              style={{
                fontSize: "clamp(14px, 4vw, 18px)",
                padding: "20px 4px",
                letterSpacing: "-0.005em",
                background: "transparent",
              }}
            />
          </span>
          <button
            type="submit"
            disabled={!value.trim() || isPending}
            className={cn(
              "flex flex-shrink-0 items-center justify-center font-bold",
              "transition-transform duration-150",
              "hover:scale-[1.04] active:scale-[0.97]",
              "disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:scale-100",
            )}
            style={{
              background: "linear-gradient(135deg, #fde68a 0%, #fbbf24 100%)",
              color: "#0a0908",
              width: 56,
              height: 56,
              borderRadius: 14,
              boxShadow: "0 6px 16px rgba(251,191,36,0.3)",
            }}
            aria-label={isPending ? "Searching" : "Search"}
          >
            {isPending ? (
              <span
                aria-hidden="true"
                className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
              />
            ) : (
              <ArrowRight className="h-5 w-5" strokeWidth={2.5} />
            )}
          </button>
        </div>
      </form>
    </>
  );
}
